import { MatchupCorner } from 'api/graphql-global-types';
import {
  GetStreamMatchups_getStreamMatchups,
  GetStreamMatchups_getStreamMatchups_competitors,
} from 'api/matchup/types/GetStreamMatchups';
import { GetStreamVoteResults_getStreamVoteResults } from 'api/matchup/types/GetStreamVoteResults';

export type Matchup = GetStreamMatchups_getStreamMatchups;
export type MatchupCompetitor = GetStreamMatchups_getStreamMatchups_competitors;
export type MatchupResults = GetStreamVoteResults_getStreamVoteResults;

export const UNKNOWN_COMPETITOR_NAME = 'TBD';

export type CornerBreakdown = {
  votes: number;
  percentage: number;
};

export type VoteBreakdown = {
  total: number;
  hasVoted: boolean;
  myVoteCompetitorId: string | null;
  cornerA: CornerBreakdown;
  cornerB: CornerBreakdown;
};

export const getCompetitorDisplayName = (
  competitor: MatchupCompetitor | null
): string => {
  if (!competitor) {
    return UNKNOWN_COMPETITOR_NAME;
  }

  const { store, name } = competitor;

  const storeName = store?.storeDetails?.storeName?.trim();
  if (storeName) {
    return storeName;
  }

  const fullName = [store?.firstName, store?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (fullName) {
    return fullName;
  }

  return name?.trim() || UNKNOWN_COMPETITOR_NAME;
};

export const getCompetitorShortName = (
  competitor: MatchupCompetitor | null
): string => {
  const displayName = getCompetitorDisplayName(competitor);
  const parts = displayName.split(' ').filter(Boolean);

  return (parts[parts.length - 1] || displayName).toUpperCase();
};

export const getCornerCompetitor = (
  matchup: Matchup,
  corner: MatchupCorner
): MatchupCompetitor | null =>
  matchup.competitors?.find((competitor) => competitor.corner === corner) ||
  null;

export const getBoutLabel = (matchup: Matchup, boutNumber: number): string =>
  matchup.isMainEvent ? 'MAIN EVENT' : `BOUT #${boutNumber}`;

export const getBoutNumbers = (matchups: Matchup[]): number[] => {
  let counter = 0;

  return matchups.map((matchup) => {
    if (matchup.isMainEvent) {
      return 0;
    }

    counter += 1;

    return counter;
  });
};

export const sortMatchups = (matchups: Matchup[]): Matchup[] =>
  [...matchups].sort((a, b) => {
    if (a.isMainEvent !== b.isMainEvent) {
      return a.isMainEvent ? -1 : 1;
    }

    return a.order - b.order;
  });

const getVotesFor = (
  results: MatchupResults | null,
  competitorId: string | null
): number => {
  if (!results || !competitorId) {
    return 0;
  }

  return (
    results.competitorVotes.find((entry) => entry.competitorId === competitorId)
      ?.votes || 0
  );
};

export const getVoteBreakdown = (
  results: MatchupResults | null,
  cornerACompetitorId: string | null,
  cornerBCompetitorId: string | null
): VoteBreakdown => {
  const cornerAVotes = getVotesFor(results, cornerACompetitorId);
  const cornerBVotes = getVotesFor(results, cornerBCompetitorId);

  const pairTotal = cornerAVotes + cornerBVotes;
  const cornerAPercentage = pairTotal
    ? Math.round((cornerAVotes / pairTotal) * 100)
    : 0;
  const cornerBPercentage = pairTotal ? 100 - cornerAPercentage : 0;

  return {
    total: results?.total || 0,
    hasVoted: Boolean(results?.myVoteCompetitorId),
    myVoteCompetitorId: results?.myVoteCompetitorId || null,
    cornerA: { votes: cornerAVotes, percentage: cornerAPercentage },
    cornerB: { votes: cornerBVotes, percentage: cornerBPercentage },
  };
};
